import { cn } from '@invent/wl-ui-kit-next';
import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted } from '@/design';
import type {
  IFigureSeries,
  IThreshold,
  IThresholdCondition
} from '../../api/metrics-updates-contracts';
import { metricsUpdatesApi } from '../../api/metrics-updates-api';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { plainNumber } from '../../concern-predicate';
import { conditionsOf, seriesKey, seriesLabel, thresholdWords } from '../../metrics-updates';
import { TEAM_PATHS } from '../../paths';
import { refusalMessage } from '../../refusal';
import { useRanking } from './ranking';

/**
 * An edition's Metrics threshold tab (SR-315): the figures the Metrics updates lists read the
 * edition by, edited on a page of its own — a territorial list's bands outgrew a dialog.
 *
 * **One set of figures, a row of amounts per band.** The figures are chosen once (survey-api's
 * catalogue, `figureSeries`), met any or all, with one margin; each band is a row of amounts, an
 * empty cell no condition — Salesforce's BIS bands were one figure at four amounts. On a
 * territorial list **every territory is drawn once** and takes one band, or none — judged then by
 * "Every other territory"; several ticked are assigned at once. A band with no territory cannot be
 * saved, and a national list draws its one row alone.
 *
 * **It judges nothing**: an amount goes as a JSON number where it is plain decimal text and as typed
 * where not, and survey-api's 400 says what it refuses. An empty threshold opens on the edition's
 * preset — last year's edition of the list's, else the survey's. The edition's `ETag` is quoted as
 * `If-Match`, and the answer adopted as the record's.
 */

interface IBand {
  key: number;
  /** Series key (`metric|scope`) → the amount as typed. */
  amounts: Record<string, string>;
}

const DEFAULT_BAND = 0;
const REASON_ID = 'threshold-not-permitted';
let nextKey = DEFAULT_BAND;

function amountsOf(conditions: readonly IThresholdCondition[]): Record<string, string> {
  return Object.fromEntries(
    conditions.map((condition) => [
      seriesKey(condition),
      condition.min === null ? '' : condition.min.toLocaleString('en-US')
    ])
  );
}

/** A band's letter — A, B, … — by its place. */
function bandName(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}

/** The band tones, by place: the chips' palette, washed. */
const BAND_TONES = [
  'bg-[var(--color-slight-blue)] text-[var(--color-royal-blue)]',
  'bg-[color-mix(in_srgb,var(--color-teal)_14%,white)] text-[var(--color-teal)]',
  'bg-[color-mix(in_srgb,var(--color-yellow)_35%,white)] text-[var(--color-midnight-blue)]',
  'bg-[color-mix(in_srgb,var(--color-magenta)_12%,white)] text-[var(--color-deep-magenta)]'
];

interface IDraft {
  series: string[];
  combine: IThreshold['combine'];
  margin: string;
  rest: Record<string, string>;
  bands: IBand[];
  /** Territory → the band key holding it. */
  assigned: Record<string, number>;
}

function draftOf(threshold: Nullable<IThreshold>): IDraft {
  const groups = threshold?.groups ?? [];
  const bands = groups.map((group) => ({ key: ++nextKey, amounts: amountsOf(group.conditions) }));
  const assigned: Record<string, number> = {};
  groups.forEach((group, index) => {
    for (const territory of group.territories) assigned[territory] = bands[index].key;
  });
  return {
    series: [...new Set(conditionsOf(threshold).map(seriesKey))],
    combine: threshold?.combine ?? 'any',
    margin: threshold?.marginPercent ? String(threshold.marginPercent) : '',
    rest: amountsOf(threshold?.conditions ?? []),
    bands,
    assigned
  };
}

/** The draft as `PUT …/threshold` takes it: a condition for each figure a row fills. */
function bodyOf(draft: IDraft, territorial: boolean, live: ReadonlySet<string>): unknown {
  const conditions = (amounts: Record<string, string>) =>
    draft.series
      .filter((key) => (amounts[key] ?? '').trim() !== '')
      .map((key) => {
        const [metric, scope] = key.split('|');
        const typed = amounts[key].trim();
        return { metric, scope, min: plainNumber(typed) ?? typed };
      });
  const margin = draft.margin.trim();
  return {
    conditions: conditions(draft.rest),
    combine: draft.combine,
    marginPercent: margin === '' ? 0 : (plainNumber(margin) ?? margin),
    ...(territorial
      ? {
          groups: draft.bands.map((band) => ({
            territories: Object.keys(draft.assigned).filter(
              (territory) => draft.assigned[territory] === band.key && live.has(territory)
            ),
            conditions: conditions(band.amounts)
          }))
        }
      : {})
  };
}

export function RankingThresholdTab() {
  const record = useRanking();
  const { key, overview, etag, permitted, territorial } = record;
  const year = Number(key.year);
  const stored = overview?.ranking.metricThreshold ?? null;

  const round = useAdminData((signal) => metricsUpdatesApi.round(key.surveyCode, signal), [key.surveyCode]);
  const vocabulary = useAdminData(
    async (signal) =>
      territorial ? ((await rankingProcessApi.vocabulary(key.surveyCode, key.list, signal))?.body ?? null) : null,
    [key.surveyCode, key.list, territorial]
  );
  const updates = round.data;
  const edition = updates?.editions.find((entry) => entry.list === key.list && entry.year === year) ?? null;
  const preset = edition?.thresholdPreset ?? updates?.thresholdPreset ?? null;
  const catalogue: IFigureSeries[] = updates?.figureSeries ?? [];
  const territories = (vocabulary.data?.territories ?? []).filter((entry) => !entry.retired).map((entry) => entry.name);

  const [draft, setDraft] = React.useState<Nullable<IDraft>>(null);
  const [dirty, setDirty] = React.useState(false);
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<{ text: string; failed: boolean }>>(null);

  // The draft starts from what is stored, else the preset — and follows a re-read while untouched.
  React.useEffect(() => {
    if (dirty || !overview || !updates) return;
    const start = draftOf(stored ?? preset);
    if (start.series.length === 0 && catalogue.length > 0) start.series = [seriesKey(catalogue[0])];
    setDraft(start);
  }, [overview, updates, stored, preset, dirty]);

  if (!overview) return null;
  if (round.error) return <LoadError message={round.error} />;
  if (!draft) return <p className="text-sm text-muted-foreground">Reading the threshold…</p>;

  const change = (patch: Partial<IDraft>) => {
    setDraft((now) => (now ? { ...now, ...patch } : now));
    setDirty(true);
    setSaid(null);
  };
  const setAmount = (bandKey: number, column: string, value: string) =>
    bandKey === DEFAULT_BAND
      ? change({ rest: { ...draft.rest, [column]: value } })
      : change({
          bands: draft.bands.map((band) =>
            band.key === bandKey ? { ...band, amounts: { ...band.amounts, [column]: value } } : band
          )
        });
  const assign = (names: Iterable<string>, bandKey: number) => {
    const assigned = { ...draft.assigned };
    for (const name of names) {
      if (bandKey === DEFAULT_BAND) delete assigned[name];
      else assigned[name] = bandKey;
    }
    change({ assigned });
  };
  // Only the vocabulary's live territories are drawn and sent: one a preset or an older save
  // names, since retired or renamed, is dropped — and said — rather than refused at Save.
  const live = new Set(territories);
  const dropped = Object.keys(draft.assigned).filter((name) => !live.has(name));
  const heldCount = (bandKey: number) =>
    Object.entries(draft.assigned).filter(([name, held]) => held === bandKey && live.has(name)).length;
  const bandIndex = (bandKey: number) => draft.bands.findIndex((band) => band.key === bandKey);
  const empties = draft.bands.filter((band) => heldCount(band.key) === 0).map((band) => bandName(bandIndex(band.key)));
  const unfilled = draft.series.length === 0;
  const readOnly = !permitted || etag === null;
  const describedBy = permitted ? undefined : REASON_ID;

  const save = async (threshold: Nullable<unknown>) => {
    if (etag === null || busy) return;
    setBusy(true);
    setSaid(null);
    try {
      const answer = await rankingProcessApi.setThreshold(
        { surveyCode: key.surveyCode, list: key.list, year },
        etag,
        threshold as Nullable<IThreshold>
      );
      record.adoptRead(answer, false);
      setDirty(false);
      setSaid({ text: threshold === null ? 'Threshold cleared.' : 'Threshold saved.', failed: false });
    } catch (error) {
      setSaid({ text: refusalMessage(error, 'The threshold could not be saved.'), failed: true });
    } finally {
      setBusy(false);
    }
  };

  const label = (column: string) => {
    const [metric, scope] = column.split('|');
    return seriesLabel(catalogue, metric, scope);
  };
  const amountCells = (bandKey: number, amounts: Record<string, string>, rowName: string) =>
    draft.series.map((column) => (
      <td key={column} className="px-2 py-1.5">
        <Input
          aria-label={`${rowName}: ${label(column)}, in dollars`}
          className="h-8 w-40"
          inputMode="decimal"
          placeholder="—"
          disabled={readOnly}
          value={amounts[column] ?? ''}
          onChange={(event) => setAmount(bandKey, column, event.target.value)}
        />
      </td>
    ));

  return (
    <div className="grid max-w-6xl gap-6" data-threshold-page>
      <p className="text-sm text-muted-foreground">
        The figures {overview.ranking.name}’s contenders are expected to reach. Metrics updates lists
        the advisors at or within the margin below them whose figures are not in yet, and — as its
        exceptions — those ranked before whose figures are now under them.{' '}
        <Link className="underline underline-offset-2" to={`${TEAM_PATHS.metricsUpdates}?survey=${encodeURIComponent(key.surveyCode)}`}>
          Open Metrics updates
        </Link>
      </p>
      {!permitted && <NotPermitted id={REASON_ID} what="setting an edition’s threshold" />}
      {stored && !dirty && (
        <p className="text-sm" data-threshold-words>
          {thresholdWords(stored, catalogue)}
        </p>
      )}
      {!edition && updates && (
        <p className="text-sm text-muted-foreground">
          This edition is not on the cycle Metrics updates is collecting now, which reads that cycle’s editions alone.
        </p>
      )}

      <section className="grid gap-3" aria-labelledby="threshold-figures">
        <h2 id="threshold-figures" className="text-base font-semibold">
          Figures
        </h2>
        <div className="flex flex-wrap items-center gap-2" data-threshold-series>
          {draft.series.map((column) => (
            <span key={column} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-sm">
              {label(column)}
              <button
                type="button"
                aria-label={`Remove ${label(column)}`}
                disabled={readOnly}
                aria-describedby={describedBy}
                className="text-muted-foreground hover:text-foreground disabled:opacity-40"
                onClick={() => change({ series: draft.series.filter((other) => other !== column) })}
              >
                ×
              </button>
            </span>
          ))}
          {catalogue.some((entry) => !draft.series.includes(seriesKey(entry))) && (
            <select
              aria-label="Add a figure"
              disabled={readOnly}
              aria-describedby={describedBy}
              className="h-9 rounded-md border border-border bg-background px-2 text-sm"
              value=""
              onChange={(event) => event.target.value && change({ series: [...draft.series, event.target.value] })}
            >
              <option value="">+ Add a figure</option>
              {catalogue
                .filter((entry) => !draft.series.includes(seriesKey(entry)))
                .map((entry) => (
                  <option key={seriesKey(entry)} value={seriesKey(entry)}>
                    {seriesLabel(catalogue, entry.metric, entry.scope)}
                  </option>
                ))}
            </select>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <fieldset className="flex items-center gap-4" disabled={readOnly}>
            <legend className="sr-only">Combine the figures</legend>
            <label className="flex items-center gap-2">
              <input type="radio" name="combine" checked={draft.combine === 'any'} onChange={() => change({ combine: 'any' })} />
              Any of them
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="combine" checked={draft.combine === 'all'} onChange={() => change({ combine: 'all' })} />
              All of them
            </label>
          </fieldset>
          <label className="flex items-center gap-2">
            <span className="text-muted-foreground">Updates list reaches down to</span>
            <Input
              className="h-8 w-20"
              inputMode="decimal"
              placeholder="0"
              disabled={readOnly}
              value={draft.margin}
              onChange={(event) => change({ margin: event.target.value })}
            />
            <span className="text-muted-foreground">% below</span>
          </label>
        </div>
      </section>

      <section className="grid gap-3" aria-labelledby="threshold-bands">
        <h2 id="threshold-bands" className="text-base font-semibold">
          {territorial ? 'Bands' : 'Amounts'}
        </h2>
        {unfilled ? (
          <p className="text-sm text-muted-foreground">Add a figure to set its amounts.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm" data-threshold-bands>
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="px-2 py-1 font-normal">{territorial ? 'Band' : ''}</th>
                  {draft.series.map((column) => (
                    <th key={column} className="px-2 py-1 font-normal">
                      {label(column)} ≥
                    </th>
                  ))}
                  {territorial && <th className="px-2 py-1 font-normal">Territories</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {territorial &&
                  draft.bands.map((band, index) => (
                    <tr key={band.key} data-threshold-band={bandName(index)}>
                      <td className="px-2 py-1.5">
                        <span className={cn('rounded px-2 py-0.5 font-semibold', BAND_TONES[index % BAND_TONES.length])}>
                          {bandName(index)}
                        </span>
                      </td>
                      {amountCells(band.key, band.amounts, `Band ${bandName(index)}`)}
                      <td className="px-2 py-1.5 text-muted-foreground">{heldCount(band.key)}</td>
                      <td className="px-2 py-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          disabled={readOnly}
                          aria-describedby={describedBy}
                          onClick={() => {
                            const assigned = Object.fromEntries(
                              Object.entries(draft.assigned).filter(([, held]) => held !== band.key)
                            );
                            change({ bands: draft.bands.filter((other) => other.key !== band.key), assigned });
                          }}
                        >
                          Remove band {bandName(index)}
                        </Button>
                      </td>
                    </tr>
                  ))}
                <tr data-threshold-band="rest">
                  <td className="px-2 py-1.5 font-medium">
                    {territorial ? 'Every other territory' : 'Every advisor'}
                  </td>
                  {amountCells(DEFAULT_BAND, draft.rest, territorial ? 'Every other territory' : 'Every advisor')}
                  {territorial && (
                    <td className="px-2 py-1.5 text-muted-foreground">
                      {territories.length - Object.keys(draft.assigned).filter((name) => live.has(name)).length}
                    </td>
                  )}
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
        {territorial && (
          <>
            <span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={readOnly}
                aria-describedby={describedBy}
                onClick={() => change({ bands: [...draft.bands, { key: ++nextKey, amounts: {} }] })}
              >
                Add a band
              </Button>
            </span>
            <p className="text-[0.8125rem] text-muted-foreground">
              A row with no amount lists nobody from its territories; “Every other territory” left
              empty lists nobody outside the bands.
            </p>
          </>
        )}
      </section>

      {territorial && (
        <section className="grid gap-3" aria-labelledby="threshold-territories">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="threshold-territories" className="text-base font-semibold">
              Territories
            </h2>
            {draft.bands.length > 0 && (
              <div className="flex items-center gap-2 text-sm" role="group" aria-label="Assign the selected territories">
                <span className="text-muted-foreground">{ticked.size} selected — assign to</span>
                {[...draft.bands.map((band, index) => ({ key: band.key, name: bandName(index) })), { key: DEFAULT_BAND, name: 'none' }].map((target) => (
                  <Button
                    key={target.key}
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={ticked.size === 0 || readOnly}
                    aria-describedby={describedBy}
                    onClick={() => {
                      assign(ticked, target.key);
                      setTicked(new Set());
                    }}
                  >
                    {target.key === DEFAULT_BAND ? 'No band' : `Band ${target.name}`}
                  </Button>
                ))}
              </div>
            )}
          </div>
          {vocabulary.error && <LoadError message={vocabulary.error} />}
          {vocabulary.data === null && !vocabulary.error && (
            <p className="text-sm text-muted-foreground">Reading the territories…</p>
          )}
          {dropped.length > 0 && vocabulary.data !== null && (
            <p className="text-sm text-muted-foreground" data-territories-dropped>
              No longer in the list’s territories, and left out on Save: {dropped.join(', ')}.
            </p>
          )}
          {vocabulary.data !== null && territories.length === 0 && !vocabulary.error && (
            <p className="text-sm text-muted-foreground">The list has no territories yet: set them on its List setup tab.</p>
          )}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-1.5" data-threshold-territories>
            {territories.map((territory) => {
              const held = draft.assigned[territory] ?? DEFAULT_BAND;
              const index = held === DEFAULT_BAND ? -1 : bandIndex(held);
              return (
                <div
                  key={territory}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-sm',
                    index >= 0 && BAND_TONES[index % BAND_TONES.length]
                  )}
                  data-territory={territory}
                  data-band={index >= 0 ? bandName(index) : ''}
                >
                  <input
                    type="checkbox"
                    aria-label={`Select ${territory}`}
                    disabled={readOnly}
                    checked={ticked.has(territory)}
                    onChange={(event) => {
                      const next = new Set(ticked);
                      if (event.target.checked) next.add(territory);
                      else next.delete(territory);
                      setTicked(next);
                    }}
                  />
                  <span className="flex-1 truncate font-medium">{territory}</span>
                  <select
                    aria-label={`${territory}: band`}
                    className="rounded border-0 bg-transparent text-sm"
                    disabled={readOnly}
                    value={held}
                    onChange={(event) => assign([territory], Number(event.target.value))}
                  >
                    <option value={DEFAULT_BAND}>—</option>
                    {draft.bands.map((band, bandAt) => (
                      <option key={band.key} value={band.key}>
                        {bandName(bandAt)}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-border bg-background py-3">
        <Button
          type="button"
          disabled={readOnly || busy || !dirty || unfilled || empties.length > 0}
          aria-describedby={describedBy}
          onClick={() => void save(bodyOf(draft, territorial, live))}
        >
          {busy ? 'Saving…' : 'Save threshold'}
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={readOnly || busy || !dirty}
          onClick={() => {
            setDirty(false);
            setTicked(new Set());
          }}
        >
          Discard changes
        </Button>
        {stored && (
          <Button type="button" variant="ghost" disabled={readOnly || busy} aria-describedby={describedBy} onClick={() => void save(null)}>
            Clear the threshold
          </Button>
        )}
        {empties.length > 0 && (
          <span className="text-sm text-muted-foreground">
            Band {empties.join(', ')} holds no territory: give it some, or remove it.
          </span>
        )}
        {!stored && !dirty && preset && (
          <span className="text-sm text-muted-foreground">Not saved yet — opened on {edition?.thresholdPreset ? 'last year’s edition' : 'the survey’s preset'}.</span>
        )}
        {said && (
          <span role={said.failed ? 'alert' : 'status'} className={cn('text-sm', said.failed ? 'text-destructive' : 'text-[var(--color-teal)]')}>
            {said.text}
          </span>
        )}
      </div>
    </div>
  );
}
